import { ITeamUserObject } from "types/TeamUsersResponse";

import { USER_TYPE_LABELS } from "./types";

/**
 * The single owner test for team-user rows.
 *
 * Reads the row's own `permission_metadata.label` rather than matching
 * `user_iam_role` against a list of owner tiers. Every owner permission set
 * (`Team_active_owner`, `Team_limited_owner`, `Team_trial_owner`,
 * `Team_legacy_owner`) is labelled "Owner" in the backend's
 * `USER_ROLES_METADATA`, and no other role is — so this stays correct when a
 * new owner tier is added, which the enumerated lists it replaces did not.
 *
 * `permission_metadata` is null for a user with no role on the team.
 */
export const isTeamOwnerRow = (
  row?: Pick<ITeamUserObject, "permission_metadata"> | null,
): boolean => row?.permission_metadata?.label === USER_TYPE_LABELS.OWNER;

/**
 * The two rows an admin must never act on: the team owner, and themselves.
 * Both are identity questions, so neither reads a role name.
 *
 * `currentUserId` is undefined until `auth/role` resolves. Callers should hold
 * back "select all" until then (see `canSelectAll`); an individual tick is
 * still possible in that window and is caught by the API's own self-removal
 * guard.
 */
export const makeIsRowSelectable =
  (currentUserId?: number) =>
  (row: ITeamUserObject): boolean =>
    !isTeamOwnerRow(row) && row.id !== currentUserId;
